import React from 'react';

import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { ToastContext, TopBar, Button, Modal } from '@weezevent/nacre';
import { datadogRum } from '@datadog/browser-rum';
import Style from '../topBarModules.module.css';
import { fetcher } from '@weezevent/weezjs-core';
import { Loader } from 'semantic-ui-react';

function LoadingCard({ loading = false, children = null, className = '' }) {
    const { t } = useTranslation();
    if (loading) {
        return (
            <div className={className}>
                <Loader style={{ zIndex: '3' }} active>
                    {t('ticket.common.loading')}
                </Loader>
            </div>
        );
    }

    return children;
}

export default function Account({ organization, deleteAccount = false }) {
    const toast = React.useContext(ToastContext);
    const auth = useAuth();
    const {
        user: { profile }
    } = auth;
    const { t } = useTranslation();
    const [openModal, setOpenModal] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const name = profile.name || `${profile.data?.firstname || ''} ${profile.data?.name || ''}`;

    const items = React.useMemo(() => {
        const components = [
            <TopBar.Account.Item
                key="account"
                primary={{
                    component: 'a',
                    href: `${auth.settings.authority}/account`,
                    target: '_blank',
                    label: t('ticket.account.manage_profil')
                }}
                mail={profile.email || profile.data?.email || ''}
                secondary={{
                    style: { color: '#000' },
                    component: 'a',
                    onClick: () => {
                        datadogRum.removeUser();
                        auth.signoutRedirect({
                            post_logout_redirect_uri: `${window.location.origin}/ticket/`
                        });
                        auth.removeUser();
                    },
                    label: t('ticket.common.action.logout')
                }}
                subtitle={t('ticket.account.card_header')}
                title={name}
            />
        ];
        if (deleteAccount) {
            components.push(
                <Button key="btn-delete" className={Style['delete-account']} onClick={() => setOpenModal(true)}>
                    {t('ticket.common.action.delete-account')}
                </Button>
            );
        }
        return components;
    }, [openModal, deleteAccount, t]);

    const triggerDeleteAccount = React.useCallback(() => {
        setLoading(true);
        fetcher
            .get(`/ticket/organizations/${organization.id}/delete_account`, {})
            .then(() => {
                // keep loading state to true to prevent user action
                setOpenModal(false);
                toast.success(t('ticket.onboarding.modale_delete.success-delete'));
                setTimeout(() => {
                    datadogRum.removeUser();
                    auth.removeUser();
                    window.location.href = 'https://weezevent.com/';
                }, 5000);
            })
            .catch(({ response }) => {
                setLoading(false);
                toast.error(t('ticket.onboarding.modale_delete.error-delete'));
                setOpenModal(false);
            });
    }, [organization]);

    return (
        <>
            {deleteAccount && (
                <LoadingCard className={Style['loading-container']} loading={loading}>
                    <Modal
                        title={t('ticket.common.action.delete-account')}
                        open={openModal}
                        onClose={() => setOpenModal(false)}
                        size="extralarge"
                        submitButton={<Button primary label={t('ticket.onboarding.modale_delete.delete_button')} onClick={triggerDeleteAccount} />}
                        cancelButton={<Button label={t('ticket.onboarding.modale_delete.cancel_button')} onClick={() => setOpenModal(false)} />}
                    >
                        {t('ticket.onboarding.modale_delete.modal_content')}
                    </Modal>
                </LoadingCard>
            )}
            <TopBar.Account tooltipTitle={t('ticket.account.tooltip')} content={name} items={items} />
        </>
    );
}
